/**
 * 売上見込み(2026-09-24 発注者指示。P-68)。
 * 発注者の言葉: 「**前日の確定した売上と人件費で原価率を算出して売上見込みを出す。人件費が確定した時点の納品が売上として原価率**」
 *
 *  - **確定した日** = その日の納品件数が入っていて、集計データもある日(＝締め作業が済んだ日)
 *  - 実績: 売上 = Σ(納品件数 × その日の単価) ／ 人件費 = Σ(報酬 ＋ 交通費。**管理者は売上原価に入れない** D-28) ／ 原価率 = 人件費 ÷ 売上
 *  - 残りの日: 会社マスタの**曜日別の納品予定**(D-145)× その日の単価 = 売上見込み
 *  - 着地: 売上 = 実績 ＋ 見込み ／ 人件費 = 着地売上 × 実績の原価率 ／ 粗利 = 売上 − 人件費
 */
import { plannedDeliveryOn, priceOn, type Company, type DateStr, type Delivery, type Yen } from '@core/lib/types';
import type { StaffMetrics } from './calc';

export type Forecast = {
  /** 確定している最後の日(この日までで原価率を出す) */
  asOf: DateStr | null;
  /** 確定した日数 / 残りの日数 */
  days_done: number;
  days_left: number;
  actual: { count: number; revenue: Yen; pay: Yen; rate: number | null };
  /** 残りの日の予定 */
  plan: { count: number; revenue: Yen };
  landing: { count: number; revenue: Yen; pay: Yen; gross: Yen };
  /** 予定が1社も入っていない(曜日別の予定が未設定)なら true */
  no_plan: boolean;
};

const sum = (list: number[]) => list.reduce((a, b) => a + b, 0);

/** 納品件数(by_style の合計) */
function deliveredOf(d: Delivery, companyId: string): number {
  const detail = d.details?.[companyId];
  if (!detail) return 0;
  return sum(Object.values(detail.by_style ?? {}).map((v) => v ?? 0));
}

export function salesForecast(args: {
  /** 見込みを出す月(YYYY-MM) */
  month: string;
  /** その月の日付(1日〜末日) */
  dates: DateStr[];
  companies: Company[];
  deliveries: Delivery[];
  rows: StaffMetrics[];
}): Forecast {
  const { dates, companies, deliveries, rows } = args;
  const active = companies.filter((c) => c.status !== 'inactive');
  const byDate = new Map(deliveries.map((d) => [d.date, d]));

  /** 納品件数が入っている日 = 締め作業が済んだ日 */
  const doneDates = dates.filter((date) => {
    const d = byDate.get(date);
    if (!d) return false;
    return active.some((c) => deliveredOf(d, c.id) > 0);
  });
  const asOf = doneDates.length > 0 ? doneDates[doneDates.length - 1]! : null;

  let count = 0;
  let revenue = 0;
  for (const date of doneDates) {
    const d = byDate.get(date)!;
    for (const c of active) {
      const n = deliveredOf(d, c.id);
      if (n > 0) {
        count += n;
        revenue += Math.round(n * priceOn(c, date));
      }
    }
  }
  // 人件費 = 報酬 ＋ 交通費(管理者は売上原価に入れない。D-28)
  const doneSet = new Set(doneDates);
  const pay = Math.round(sum(rows.filter((r) => doneSet.has(r.date) && r.work_style !== 'kanri').map((r) => r.pay + r.commute)));
  const rate = revenue > 0 ? (pay / revenue) * 100 : null;

  // 残りの日(確定した最後の日の翌日〜月末)の予定
  const leftDates = asOf ? dates.filter((d) => d > asOf) : dates;
  let planCount = 0;
  let planRevenue = 0;
  let hasPlan = false;
  for (const date of leftDates) {
    for (const c of active) {
      const n = plannedDeliveryOn(c, date);
      if (n === null) continue;
      hasPlan = true;
      if (n > 0) {
        planCount += n;
        planRevenue += Math.round(n * priceOn(c, date));
      }
    }
  }

  const landingRevenue = revenue + planRevenue;
  const landingPay = rate === null ? 0 : Math.round((landingRevenue * rate) / 100);
  return {
    asOf,
    days_done: doneDates.length,
    days_left: leftDates.length,
    actual: { count, revenue, pay, rate },
    plan: { count: planCount, revenue: planRevenue },
    landing: { count: count + planCount, revenue: landingRevenue, pay: landingPay, gross: landingRevenue - landingPay },
    no_plan: !hasPlan,
  };
}
